import BIG from "@/libs/bigmin.js";
import { formatCurrencyType } from "@/features/common/currencyDisplay/CurrencyDisplay";

import USDT_PATH from "@/assets/image/wallet/wallet_usdt.svg?url";
import USD_PATH from "@/assets/image/wallet/wallet_usd.svg?url";
import FUN_PATH from "@/assets/image/wallet/wallet_fun.webp";
import BTC_PATH from "@/assets/image/wallet/wallet_btc.svg?url";
import BCH_PATH from "@/assets/image/wallet/wallet_bch.svg?url";
import KE_WALLET_ICON from "@/assets/country/ke/wallet/icon.svg?url";
import GH_WALLET_ICON from "@/assets/country/gh/wallet/icon.svg?url";
import ZM_WALLET_ICON from "@/assets/country/zm/wallet/icon.svg?url";
import NG_WALLET_ICON from "@/assets/country/ng/wallet/icon.svg?url";
import TZ_WALLET_ICON from "@/assets/country/tz/wallet/icon.svg?url";
import CI_WALLET_ICON from "@/assets/country/ci/wallet/icon.svg?url";
import SN_WALLET_ICON from "@/assets/country/sn/wallet/icon.svg?url";
import TG_WALLET_ICON from "@/assets/country/tg/wallet/icon.svg?url";
import BJ_WALLET_ICON from "@/assets/country/bj/wallet/icon.svg?url";
import BF_WALLET_ICON from "@/assets/country/bf/wallet/icon.svg?url";
import EG_WALLET_ICON from "@/assets/country/eg/wallet/icon.svg?url";
import MX_WALLET_ICON from "@/assets/country/mx/wallet/icon.svg?url";
import { CurrencyType } from "@/api/proto/IFCommon";
import {
  getCurrentCountryCode,
  getCurrentCurrency,
} from "@/utils/countryConfigUtils";

/** Crypto currency list: these currency types do not need BRL conversion */
const CRYPTO_CURRENCIES = ["USDT", "BTC", "BCH", "ETH", "FUN"];

/**
 * Infer the server currency type.
 * If serverCurrencyType is not provided and currencyType is local, default to BRL.
 */
const inferServerCurrencyType = (
  currencyType: CurrencyType | undefined,
  serverCurrencyType: CurrencyType | undefined
): CurrencyType | undefined => {
  if (serverCurrencyType) {
    return serverCurrencyType;
  }

  if (!currencyType) {
    return undefined;
  }

  // Crypto currencies do not need conversion.
  if (CRYPTO_CURRENCIES.includes(currencyType)) {
    return currencyType;
  }

  // If this is a local currency, the server defaults to BRL.
  const localCurrency = getCurrentCurrency();
  if (currencyType === localCurrency) {
    return "BRL";
  }

  return currencyType;
};

// Expose inference for callers that don't have a wallet object yet (e.g. home footer).
export const inferServerCurrencyTypeForDisplay = (
  currencyType: CurrencyType | undefined,
  serverCurrencyType?: CurrencyType | undefined
): CurrencyType | undefined => inferServerCurrencyType(currencyType, serverCurrencyType);

/** Get default currency type (dynamic, based on current country config). */
export const getDefaultCurrencyType = () => getCurrentCurrency();
/**
 * @deprecated Use getDefaultCurrencyType() instead for dynamic currency.
 * Kept as a getter for backward compatibility — returns current country's currency.
 */
export const DefaultCurrencyType = getCurrentCurrency();
/** Base unit for server amount precision. */
const BaseAmountUnit = new BIG(10000000000);
/** Thousands separator format. */
const AmountReg = /(\d)(?=(\d{3})+\.)/g;

export const CurrencyImage = {
  USDT: USDT_PATH,
  USD: USD_PATH,
  BTC: BTC_PATH,
  BCH: BCH_PATH,
  FUN: FUN_PATH,
  LOCK_FUN: FUN_PATH,
};

const CountryFiatCurrencyImage: Record<string, string> = {
  dev: KE_WALLET_ICON,
  ke: KE_WALLET_ICON,
  gh: GH_WALLET_ICON,
  zm: ZM_WALLET_ICON,
  ng: NG_WALLET_ICON,
  tz: TZ_WALLET_ICON,
  ci: CI_WALLET_ICON,
  sn: SN_WALLET_ICON,
  tg: TG_WALLET_ICON,
  bj: BJ_WALLET_ICON,
  bf: BF_WALLET_ICON,
  eg: EG_WALLET_ICON,
  mx: MX_WALLET_ICON,
};

const getCurrentFiatCurrencyImagePath = () => {
  const countryCode = getCurrentCountryCode() || "ke";
  return CountryFiatCurrencyImage[countryCode] || KE_WALLET_ICON;
};

export interface CurrencyExtra {
  currencyType: CurrencyType;
  serverCurrencyType?: CurrencyType;
}

export const toLocaleString = (num: string | number): any => {
  return num;
};

export const NumberMinus = (num1: number, num2: number) => {
  const n1 = new BIG(num1);
  const p = n1.minus(num2);
  return p.toString();
};

/** Convert scientific notation to a normal string. */
export const transferToNumber = (num: string | number): string => {
  if (isNaN(Number(num))) {
    return num + "";
  }
  num = num + "";
  num = parseFloat(num);
  const eformat = num.toExponential();
  const tmpArray: any = eformat.match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return toLocaleString(
    Number(num.toFixed(Math.max(0, (tmpArray[1] || "").length - tmpArray[2])))
  );
};

/** Multiply amount for server-side precision. */
export const AmountMultiplierConversion = (
  amount: string | number,
  extra: CurrencyExtra
): number => {
  const { currencyType, serverCurrencyType } = extra || {};
  const tAmount = Number(amount);

  if (isNaN(tAmount)) {
    return amount as any;
  }

  // Infer server currency type (auto-convert local currency to BRL).
  const precisionCurrency = inferServerCurrencyType(
    currencyType,
    serverCurrencyType
  );

  // If currency or precision map is missing, fall back to BaseAmountUnit.
  if (!precisionCurrency || !window.currencyPrecision) {
    const retAmount = new BIG(tAmount).times(BaseAmountUnit);
    return Number(retAmount.toString());
  }

  const rate = window.currencyPrecision[precisionCurrency];
  if (rate) {
    const retAmount = new BIG(tAmount).times(new BIG(rate));
    return Number(retAmount.toString());
  } else {
    const retAmount = new BIG(tAmount).times(BaseAmountUnit);
    console.error(
      `No matching precision for ${precisionCurrency} (display currency ${currencyType}).`
    );
    return Number(retAmount.toString());
  }
};

/** Divide amount for display from server-side precision. */
export const AmountDivisorConversion = (
  amount: string | number,
  extra: CurrencyExtra
): number => {
  const { currencyType, serverCurrencyType } = extra || {};
  const tAmount = Number(amount);

  if (isNaN(tAmount)) {
    return amount as any;
  }

  if (tAmount === 0) {
    return 0;
  }

  // Infer server currency type (auto-convert local currency to BRL).
  const precisionCurrency = inferServerCurrencyType(
    currencyType,
    serverCurrencyType
  );

  // If currency or precision map is missing, fall back to BaseAmountUnit.
  if (!precisionCurrency || !window.currencyPrecision) {
    const retAmount = new BIG(tAmount).div(BaseAmountUnit);
    return Number(retAmount.toFixed(9, 0));
  }

  const rate = window.currencyPrecision[precisionCurrency];
  if (rate) {
    const retAmount = new BIG(tAmount).div(new BIG(rate));
    return Number(retAmount.toFixed(9, 0));
  } else {
    const retAmount = new BIG(tAmount).div(BaseAmountUnit);
    console.error(
      `No matching precision for ${precisionCurrency} (display currency ${currencyType}).`
    );
    return Number(retAmount.toFixed(9, 0));
  }
};

export const numberTimes = (num1: number, num2: number, fixed = 9) => {
  const tnum1 = new BIG(num1).times(BaseAmountUnit);
  const tnum2 = new BIG(num2).times(BaseAmountUnit);
  return Number(
    tnum1.times(tnum2).div(BaseAmountUnit).div(BaseAmountUnit).toFixed(fixed, 0)
  );
};

export const numberDivisor = (num1: number, num2: number, fixed = 9) => {
  const tnum1 = new BIG(num1);
  const tnum2 = new BIG(num2);
  if (num1 == 0 || num2 == 0) {
    return tnum2.toFixed(fixed, 0);
  }
  return Number(tnum1.div(tnum2).toFixed(fixed, 0));
};

/**
 * Clamp a number string to a max total length (including decimal part).
 * @param num Input value.
 * @param valid Max length.
 */
export const dealNumberValid = (num, valid = 9): string => {
  const tNum = Number(num);
  if (!isNaN(tNum)) {
    const bigNum = new BIG(num);
    const bigStr = String(transferToNumber(bigNum.toString()));
    const hasDot = bigStr.includes(".");
    const strLength = bigStr.length;
    hasDot && (valid += 1);
    let retValue = bigStr;
    if (strLength <= valid) {
      if (hasDot) {
        retValue =
          bigStr + "0000000000000000000".substring(0, valid - strLength);
      } else {
        if (valid === strLength) {
          retValue =
            bigStr + "0000000000000000000".substring(0, valid - strLength);
        } else {
          retValue =
            bigStr +
            "." +
            "0000000000000000000".substring(0, valid - strLength);
        }
      }
    } else {
      if (hasDot) {
        const dotIndex = retValue.indexOf(".");
        if (dotIndex >= valid - 1) {
          return bigStr.substring(0, dotIndex);
        } else {
          return bigStr.substring(0, valid);
        }
      }
    }
    return retValue;
  }
  return num + "";
};

/**
 * Fix decimal places for display.
 * @param num Input value.
 * @param fixed Decimal places to keep.
 */
export const numberFixed = (num, fixed = 2): string => {
  const tNum = Number(num);
  if (!isNaN(tNum)) {
    const bigNum = new BIG(num);
    const fixedNum = bigNum.toFixed(fixed, 0);
    return toLocaleString(fixedNum);
  }
  return num;
};

/**
 * Handle special number formats (show two decimals by default; if all 9s,
 * extend until the first non-9 digit).
 * @param num Input value.
 * @param minDigits Minimum decimal places.
 */
export const processSpecialNumber = (
  num: number | string,
  minDigits = 2
): string => {
  const tNum = Number(num);
  if (isNaN(tNum)) return String(num);

  // Keep enough decimal places for analysis.
  const bigNum = new BIG(num);
  const fixedNum = bigNum.toFixed(10, 0);

  // Inspect decimal part.
  const parts = fixedNum.split(".");
  if (parts.length !== 2) return toLocaleString(bigNum.toFixed(minDigits, 0));

  const decimalPart = parts[1];

  // If decimal part starts with 99..., find the first non-9 position.
  if (decimalPart.startsWith("99")) {
    let nonNinePos = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] !== "9") {
        nonNinePos = i;
        break;
      }
    }

    // Keep decimals up to the first non-9 digit.
    if (nonNinePos > 0) {
      return toLocaleString(bigNum.toFixed(nonNinePos + 1, 0));
    }
  }

  // Default to minDigits decimals.
  return toLocaleString(bigNum.toFixed(minDigits, 0));
};

/**
 * Amount display with 2 decimals and conversion.
 */
export const dealAmount9Valid = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  //amount = dealNumberValid(amount, 9);
  amount = numberFixed(amount, 2);
  return amount;
};
/**
 * Amount display with a custom number of decimals.
 */
export const dealAmountAnyValid = (
  amount: string | number,
  len: number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  //amount = dealNumberValid(amount, len);
  amount = numberFixed(amount, len);
  return amount;
};
/** Amount display with 2 decimals. */
export const dealAmount2Fiexd = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 2);
  return amount;
};
/** Amount display with 4 decimals. */
export const dealAmount4Fixed = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 4);
  return amount;
};
/** Amount display with 0 decimals. */
export const dealAmount0Fiexd = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 0);
  return amount;
};
/** Amount display with 2 decimals and thousands separators. */
export const dealAmountSegmentationAnd2Fixed = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 2);
  amount = amount.replace(AmountReg, "$1,");
  return amount;
};

/** Amount display with currency type prefix. */
export const dealAmount9ValidCurrency = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  const { currencyType } = extra || {};
  amount = dealAmount9Valid(amount, extra);
  const type = formatCurrencyType(currencyType);
  return type + " " + amount;
};

/** Amount display with currency type and 2 decimals. */
export const dealAmount2FiexdCurrency = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  const { currencyType } = extra || {};
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 2);
  const type = formatCurrencyType(currencyType);
  return type + " " + amount;
};

/** Amount display with 2 decimals, thousands separators, and currency type. */
export const dealAmountSegmentationAnd2FixedCurrency = (
  amount: string | number,
  extra: CurrencyExtra
): string => {
  const { currencyType } = extra || {};
  amount = AmountDivisorConversion(amount, extra);
  amount = numberFixed(amount, 2);
  amount = amount.replace(AmountReg, "$1,");
  const type = formatCurrencyType(currencyType);
  return type + " " + amount;
};

export const getCurrencyImagePath = (type?: string) => {
  const currentCurrency = getCurrentCurrency();
  if (!type || type === "BRL" || type === currentCurrency) {
    return getCurrentFiatCurrencyImagePath();
  }
  return CurrencyImage[type] || getCurrentFiatCurrencyImagePath();
};

/** Exchange rate conversion. */
export const exchangeRateConversion = (
  source: { amount: string | number; currency: string },
  targetCurrency: string,
  rate: number
) => {
  let newValue = Number(source?.amount) * rate;
  const sourcePrecision =
    window.currencyPrecision?.[source?.currency] ||
    Number(BaseAmountUnit.toString());
  const targetPrecision =
    window.currencyPrecision?.[targetCurrency] ||
    Number(BaseAmountUnit.toString());

  if (sourcePrecision === 0 || targetPrecision === 0) {
    return newValue;
  }

  let deltaPrecision = sourcePrecision / targetPrecision;
  if (deltaPrecision > 0) {
    newValue = newValue / deltaPrecision;
  } else if (sourcePrecision > 0) {
    deltaPrecision = targetPrecision / sourcePrecision;
    newValue = newValue * deltaPrecision;
  }
  return newValue;
};
